<script setup>
import {getKitSettings} from "@/composables/kit-settings/useKitSettings";
import {computed, ref} from "vue";
import moment from "moment";
import {useKitPages} from "@/composables/kit-settings/useKitPages";
import {kitName} from "@/composables/useLocalDomains";

const props = defineProps({
  index: {
    type: [Number, null],
    required: true,
  },
  page: {
    type: Object,
    required: true,
  },
})

const {data} = getKitSettings();
const {removePriorityPage} = useKitPages();
const {mutate: removePage, isPending} = removePriorityPage();

const pageCount = computed(() => data?.value?.package_deliverable_pages_count ?? null);
const startDate = computed(() => data.value?.day_six_date ? moment(data.value.day_six_date) : null);
const isCurrentMonth = computed(() => props.index < pageCount.value)
const canBeProcessed = computed(() => !!props.page.has_kit_fixable_actions)

const getMonthForIndex = (index) => {
  if (pageCount.value <= 0) {
    throw new Error("Counter must be greater than 0");
  }

  const monthOffset = Math.floor(index / pageCount.value);

  const targetDate = startDate.value.clone().add(monthOffset, 'month');

  return targetDate.format('MMMM');
}

const hovered = ref(false);
const starIcon = computed(() => hovered.value ? 'icons/gold-star' : 'icons/gold-star-filled');
const calendarIcon = computed(() => isCurrentMonth.value ? 'icons/calendar-outline' : 'icons/calendar-next-arrow')

const rowClass = computed(() => {
  if (!canBeProcessed.value) {
    return 'tw-border-neutral-300'
  }

  if (isCurrentMonth.value) {
    return 'tw-border-caution-600 tw-bg-caution-100';
  }

  return 'tw-border-info-300 tw-bg-info-100';
});

const pillClasses = 'tw-flex tw-rounded-full tw-font-bold tw-px-4 tw-py-2 tw-min-w-[8rem] tw-gap-2 tw-items-center'
</script>

<template>
  <li
    v-if="pageCount && startDate"
    :class="[
      'tw-w-full tw-flex tw-justify-between tw-items-center tw-px-8 tw-py-6 tw-border tw-rounded-xl tw-cursor-pointer tw-transition-all',
      rowClass,
      {'tw-cursor-progress tw-pointer-events-none': isPending},
    ]"
    @click="() => {}"
  >
    <div class="tw-flex tw-w-full tw-justify-between tw-items-center">
      <div class="tw-flex tw-gap-3 tw-min-w-0">
        <svg-vue
          v-tooltip="{
            content: 'Click to remove this page from the priority list.',
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
          class="tw-h-6 tw-min-w-6 tw-cursor-pointer"
          :icon="starIcon"
          @mouseenter="hovered = true"
          @mouseleave="hovered = false"
          @click.stop="removePage(page.page_id)"
        />

        <div
          v-tooltip="{
            content: page.url,
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
          class="tw-text-base tw-font-bold tw-text-neutral-800 tw-min-w-0 tw-truncate tw-mr-2"
        >
          {{ page.url }}
        </div>
      </div>

      <div class="tw-flex tw-gap-8 tw-items-center">
        <div
          v-if="canBeProcessed"
          v-tooltip="{
            content: `This page will be processed by ${kitName} in ${getMonthForIndex(index)}`,
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
          :class="[
            'tw-text-white',
            pillClasses,
            isCurrentMonth ? 'tw-bg-caution-600' : 'tw-bg-info-300',
          ]"
        >
          <svg-vue
            class="tw-h-5"
            :icon="calendarIcon"
          />
          <div class="tw-mx-auto">
            {{ getMonthForIndex(index) }}
          </div>
        </div>

        <div
          v-else
          v-tooltip="{
            content: `
              There's nothing for ${kitName} to work on for this page at the moment.<br>
              It will stay in this position and be worked on automatically when updates are available.
            `,
            html: true,
            placement: 'bottom',
            theme: 'dark-blue-tooltip',
          }"
          :class="[
            'tw-text-danger-600 tw-bg-danger-100',
            pillClasses
          ]"
        >
          <svg-vue
            class="tw-h-6 tw-min-w-6"
            icon="icons/warning-circle"
          />
          <div class="tw-mx-auto">
            No Actions
          </div>
        </div>

        <svg-vue
          :class="[
            'tw-h-8',
            {
              'tw-text-caution-700': isCurrentMonth && canBeProcessed,
              'tw-text-info-700': !isCurrentMonth && canBeProcessed,
              'tw-text-neutral-500': !canBeProcessed,
            }
          ]"
          icon="icons/dots-six-vertical"
        />
      </div>
    </div>
  </li>
</template>
